import React, { useState, useEffect, useCallback } from 'react';
import { User, CheckCircle, CreditCard, Award, Eye, Download, FileText } from 'lucide-react';
import FALPreviewModal from './FALPreviewModal';
import { downloadFALPDF, downloadKYPPDFFromFirestore, downloadKYPPDF } from '../../utils/pdfUtils';

/**
 * SanctionLettersSection
 * Unified section to display sanction letters (KYP, FAL, Disbursal, Closure) with modern preview.
 * - FAL uses live React-PDF modal (Comprehensive template) via FALPreviewModal
 * - KYP generated dynamically (comprehensive) on view/download
 * - Other letters rely on stored sanction entries (url) for preview/download
 *
 * Props:
 *  - application: full application object
 *  - sanctions: array of sanction records from Firestore (type, url, generatedAt, etc.)
 *  - onRefresh?: callback to re-fetch sanctions after operations
 *  - formatDate: function to format timestamps
 */
const SanctionLettersSection = ({ application, sanctions = [], onRefresh, formatDate }) => {
  const [showFALPreview, setShowFALPreview] = useState(false);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const availableLetters = [
    { id: 'kyp', title: 'KYP Letter', description: 'Know Your Patient verification letter', icon: User, color: 'blue' },
    { id: 'approval', title: 'Final Approval Letter', description: 'Application approval confirmation', icon: CheckCircle, color: 'green' },
    { id: 'disbursal', title: 'Disbursal Letter', description: 'Treatment cost disbursal authorization', icon: CreditCard, color: 'purple' },
    { id: 'closure', title: 'Case Closure Letter', description: 'Final case closure and settlement', icon: Award, color: 'orange' }
  ];

  const resolveLetterRecord = useCallback((letter) => {
    let existingLetter;
    if (letter.id === 'kyp' && application?.workflow?.medicalVerification?.kypGenerated) {
      existingLetter = {
        id: 'kyp',
        type: 'kyp',
        title: 'KYP Letter',
        url: null,
        fileName: 'KYP-Letter.pdf',
        generatedAt: application.workflow.medicalVerification.kypGeneratedAt || application.workflow.medicalVerification.verifiedAt || new Date(),
        generatedBy: 'Medical Team',
        isKYPLetter: true,
        kypData: application.workflow.medicalVerification.kypData,
        status: application.workflow?.medicalVerification?.doctorApproval?.status || 'pending'
      };
    } else {
      existingLetter = sanctions.find(s => s.type === letter.id);
    }

    if (!existingLetter && letter.id === 'approval' && application?.workflow?.dischargeSettlement?.finalApprovalLetterUrl) {
      existingLetter = {
        id: 'approval-quicklink',
        type: 'approval',
        title: 'Final Approval Letter',
        url: application.workflow.dischargeSettlement.finalApprovalLetterUrl,
        fileName: 'Final-Approval-Letter.pdf',
        generatedAt: application.workflow?.dischargeSettlement?.processedAt || application.updatedAt || new Date(),
        generatedBy: 'Financial Team',
        status: 'approved'
      };
    }
    return existingLetter;
  }, [application, sanctions]);

  const openLetter = async (letter) => {
    // FAL handled by dedicated React-PDF modal
    if (letter.type === 'approval' || letter.id === 'approval' || (letter.title || '').toLowerCase().includes('final approval')) {
      setShowFALPreview(true);
      return;
    }
    // KYP dynamic generation
    if (letter?.isKYPLetter || letter?.type === 'kyp') {
      try {
        setIsProcessing(true);
        const result = await downloadKYPPDFFromFirestore(application?.applicationId, { template: 'comprehensive' });
        if (!result?.success) {
          // fallback to direct generation
          await downloadKYPPDF(application, letter.kypData, `KYP_Comprehensive_${application?.applicationId || Date.now()}_${Date.now()}.pdf`, { template: 'comprehensive' });
        }
      } catch (e) {
        console.error('KYP preview failed, attempting download fallback:', e);
      } finally {
        setIsProcessing(false);
      }
      return;
    }
    // Other letters open inline (simple iframe via modal)
    setSelectedLetter(letter);
    setShowLetterModal(true);
  };

  const handleLetterDownload = async (letter) => {
    try {
      setIsProcessing(true);
      if (letter?.isKYPLetter || letter?.type === 'kyp') {
        const byId = await downloadKYPPDFFromFirestore(application?.applicationId, { template: 'comprehensive' });
        if (!byId?.success) {
          await downloadKYPPDF(application, letter.kypData, `KYP_Comprehensive_${application?.applicationId || Date.now()}_${Date.now()}.pdf`, { template: 'comprehensive' });
        }
      } else if (letter?.type === 'approval' || letter?.id === 'approval') {
        const remarks = application?.workflow?.dischargeSettlement?.comments || '';
        const res = await downloadFALPDF(application, { remarks, template: 'comprehensive' });
        if (!res?.success) throw new Error(res?.error || 'Failed FAL download');
      } else if (letter?.url) {
        const a = document.createElement('a');
        a.href = letter.url;
        a.download = letter.fileName || `${letter.title || 'letter'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      console.error('Download error:', e);
      alert('Failed to download letter.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Official Letters & Documents</h2>
          <p className="text-sm text-gray-600">View and download official letters and documents</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availableLetters.map(letter => {
          const Icon = letter.icon;
          const existingLetter = resolveLetterRecord(letter);
          const isAvailable = Boolean(existingLetter);
          const status = existingLetter?.status || existingLetter?.hospitalAcknowledgement?.status || 'pending';
          return (
            <div key={letter.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${isAvailable ? `bg-${letter.color}-100` : 'bg-gray-100'}`}> 
                    <Icon className={`h-5 w-5 ${isAvailable ? `text-${letter.color}-600` : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>{letter.title}</h3>
                    <p className={`text-sm ${isAvailable ? 'text-gray-600' : 'text-gray-400'}`}>{letter.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{isAvailable ? 'Available' : 'Pending'}</span>
                  {isAvailable && (
                    <div className="mt-1">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${status === 'accepted' || status === 'approved' ? 'bg-green-100 text-green-800' : status === 'challenged' || status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{status === 'accepted' || status === 'approved' ? 'Accepted' : status === 'challenged' || status === 'rejected' ? 'Challenged' : 'Pending Review'}</span>
                    </div>
                  )}
                </div>
              </div>
              {isAvailable ? (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-800">Letter Available</p>
                        <p className="text-xs text-green-600">Generated on {formatDate(existingLetter.generatedAt)}</p>
                        <p className="text-xs text-green-600">By {existingLetter.generatedBy}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button onClick={() => openLetter(existingLetter)} className="text-blue-600 hover:text-blue-800 text-sm" title="View">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleLetterDownload(existingLetter)} disabled={isProcessing} className="px-3 py-1 rounded text-sm flex items-center space-x-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                          <Download className="h-3 w-3" />
                          <span>{isProcessing ? '...' : 'Download'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">
                    {letter.id === 'kyp' ? 'Letter will be available once KYP is generated by medical team' :
                     letter.id === 'approval' ? 'Letter will be available once all verifications are completed' :
                     letter.id === 'disbursal' ? 'Letter will be available once application is approved' :
                     'Letter will be available once case is closed'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sanctions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document History</h3>
          <div className="space-y-3">
            {sanctions.map(sanction => (
              <div key={sanction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">{sanction.title}</p>
                    <p className="text-sm text-gray-600">Generated by {sanction.generatedBy} on {formatDate(sanction.generatedAt)}</p>
                    {sanction.hospitalAcknowledgement && (
                      <p className="text-xs mt-1">Status: <span className={`${sanction.hospitalAcknowledgement.status === 'accepted' ? 'text-green-700' : sanction.hospitalAcknowledgement.status === 'challenged' ? 'text-red-700' : 'text-yellow-700'}`}>{(sanction.hospitalAcknowledgement.status || 'pending').toUpperCase()}</span>{sanction.hospitalAcknowledgement.comment ? ` • ${sanction.hospitalAcknowledgement.comment}` : ''}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => openLetter(sanction)} className="text-blue-600 hover:text-blue-800" title="View"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => handleLetterDownload(sanction)} disabled={isProcessing} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center space-x-1 disabled:opacity-50"><Download className="h-3 w-3" /><span>{isProcessing ? '...' : 'Download'}</span></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <FALPreviewModal
        isOpen={showFALPreview}
        onClose={() => setShowFALPreview(false)}
        application={application}
        remarks={application?.workflow?.dischargeSettlement?.comments || ''}
        canDownload={true}
        template="comprehensive"
      />

      {showLetterModal && selectedLetter && selectedLetter.url && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Letter Viewer</h3>
                <p className="text-sm text-gray-600">{selectedLetter.title}</p>
              </div>
              <button onClick={() => { setShowLetterModal(false); setSelectedLetter(null); }} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="flex-1 p-4">
              <iframe src={selectedLetter.url} className="w-full h-full border border-gray-300 rounded" title="Letter Preview" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SanctionLettersSection;
